import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { captureError } from "@/lib/sentry";
import TeamLogo from "@/components/TeamLogo";
import { ChatCircleDotsIcon, ArrowsClockwiseIcon, BasketballIcon } from "@phosphor-icons/react";

// Lighten near-black team colors so shot dots stay visible on the dark court.
function safeColor(hex, fallback = "#C4CED4") {
  if (!hex || typeof hex !== "string") return fallback;
  const h = hex.replace("#", "");
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  return lum < 32 ? fallback : hex;
}

const TABS = [
  { key: "shots", label: "Shot Chart" },
  { key: "winprob", label: "Win Probability" },
  { key: "plays", label: "Plays" },
];

export default function NBAGameday({ game, gameId, language, onAskGamly }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | unavailable
  const [tab, setTab] = useState("shots");
  const [filter, setFilter] = useState("all"); // all | home | away
  const pollRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const r = await api.get(`/games/${gameId}/nba-summary`);
      if (r.data?.available) {
        setData(r.data);
        setStatus("ready");
      } else {
        setStatus("unavailable");
      }
    } catch (err) {
      captureError("nba summary fetch failed:", err);
      setStatus("unavailable");
    }
  }, [gameId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (game?.status !== "LIVE" || game?.is_demo) return;
    pollRef.current = setInterval(fetchData, 30000);
    return () => clearInterval(pollRef.current);
  }, [fetchData, game]);

  const home = data?.teams?.home;
  const away = data?.teams?.away;
  const homeColor = safeColor(home?.color, "#0A84FF");
  const awayColor = safeColor(away?.color, "#FF453A");

  return (
    <div data-testid="nba-gameday" className="rounded-xl bg-[#0b0f17] border border-white/10 overflow-hidden">
      {/* Scoreboard header */}
      <div className="px-5 py-4 flex items-center justify-between border-b border-white/10 bg-gradient-to-r from-[#10151f] to-[#0b0f17]">
        <ScoreSide team={away} color={awayColor} game={game} side="away" />
        <div className="text-center px-3">
          {game?.status === "LIVE" ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#FF0000]/15 border border-[#FF453A]/40">
              <span className="live-dot" />
              <span className="text-[10px] font-black uppercase tracking-[0.15em] text-[#FF453A]">{game.period_label}</span>
            </span>
          ) : (
            <span className="text-[10px] font-black uppercase tracking-[0.15em] text-white/60">{game?.period_label}</span>
          )}
          <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/35 mt-1">NBA · Game Day</p>
        </div>
        <ScoreSide team={home} color={homeColor} game={game} side="home" />
      </div>

      {/* Action row */}
      <div className="px-5 py-2.5 flex items-center justify-between border-b border-white/5">
        <div className="flex gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`nba-tab-${t.key}`}
              className={`px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-wider transition-colors ${
                tab === t.key ? "bg-white text-black" : "text-white/55 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {onAskGamly && (
            <button onClick={onAskGamly} data-testid="nba-ask-gamly" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-white text-[11px] font-bold uppercase tracking-wider">
              <ChatCircleDotsIcon weight="fill" size={13} /> Ask Gamly
            </button>
          )}
          {game?.status === "LIVE" && !game?.is_demo && (
            <button onClick={fetchData} data-testid="nba-refresh" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-white/15 text-white/70 text-[11px] font-bold uppercase tracking-wider hover:bg-white/5">
              <ArrowsClockwiseIcon weight="bold" size={13} />
            </button>
          )}
        </div>
      </div>

      <div className="p-5">
        {status === "loading" && <div className="text-center text-white/40 text-sm py-16" data-testid="nba-loading">Loading Game Day…</div>}
        {status === "unavailable" && (
          <div className="text-center text-white/45 text-sm py-16" data-testid="nba-unavailable">
            <BasketballIcon size={32} className="mx-auto mb-3 text-white/25" />
            Shot chart & win probability appear once the game tips off.
          </div>
        )}
        {status === "ready" && data && (
          <>
            {tab === "shots" && (
              <ShotChart shots={data.shots} away={away} home={home} awayColor={awayColor} homeColor={homeColor} filter={filter} setFilter={setFilter} />
            )}
            {tab === "winprob" && (
              <WinProbChart wp={data.win_prob} away={away} home={home} awayColor={awayColor} homeColor={homeColor} />
            )}
            {tab === "plays" && <PlaysList plays={data.recent_plays} away={away} home={home} />}
            {data.leaders?.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-3" data-testid="nba-leaders">
                {data.leaders.map((l) => (
                  <div key={`${l.side}-${l.name}`} className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/[0.04] border border-white/10">
                    <span className="w-1.5 h-5 rounded" style={{ backgroundColor: l.side === "home" ? homeColor : awayColor }} />
                    <span className="text-xs font-bold text-white/85">{l.name}</span>
                    <span className="text-xs font-num text-white/55">{l.stat} PTS</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ScoreSide({ team, color, game, side }) {
  if (!team) return <div className="w-32" />;
  const align = side === "home" ? "items-end text-right" : "items-start";
  return (
    <div className={`flex flex-col gap-0.5 ${align}`} data-testid={`nba-score-${side}`}>
      <div className={`flex items-center gap-2 ${side === "home" ? "flex-row-reverse" : ""}`}>
        <TeamLogo abbr={team.abbr} logo={team.logo || game?.[`${side}_team_logo`]} color={color} size={36} />
        <div className={side === "home" ? "text-right" : ""}>
          <p className="text-[11px] font-bold uppercase tracking-tight text-white/80 max-w-[150px] truncate">{team.name}</p>
          {team.record && <p className="text-[9px] text-white/35">{team.record}</p>}
        </div>
      </div>
      <p className="font-num text-4xl font-black leading-none mt-1">{team.score}</p>
    </div>
  );
}

// ---- Shot chart (half court) ----
function ShotChart({ shots, away, home, awayColor, homeColor, filter, setFilter }) {
  const W = 500, H = 420;
  const px = (x) => x * 10;          // x: 0-50 ft → 0-500
  const py = (y) => 52 + y * 10;     // y: 0 = baseline/hoop, grows toward midcourt
  const visible = shots.filter((s) => filter === "all" || s.side === filter);
  const madePct = shots.length ? Math.round((100 * shots.filter((s) => s.made).length) / shots.length) : 0;

  return (
    <div data-testid="nba-shot-chart">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex gap-1.5">
          {[["all", "All"], ["away", away?.abbr], ["home", home?.abbr]].map(([k, lbl]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`nba-filter-${k}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border transition-colors ${
                filter === k ? "bg-white text-black border-white" : "border-white/15 text-white/60 hover:text-white"}`}>
              {lbl}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-wider text-white/50">
          <Legend color={awayColor} label={`${away?.abbr} ${visible.filter((s)=>s.side==='away').length}`} />
          <Legend color={homeColor} label={`${home?.abbr} ${visible.filter((s)=>s.side==='home').length}`} />
          <span className="text-white/35">{madePct}% FG</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full rounded-lg" style={{ background: "linear-gradient(#13202e,#0c1620)" }}>
        {/* court markings */}
        <g stroke="#3a4d63" strokeWidth="2" fill="none">
          <rect x="2" y="2" width={W - 4} height={H - 4} rx="4" />
          <rect x="170" y="2" width="160" height="188" />
          <circle cx="250" cy="190" r="60" />
          <path d="M30,2 L30,150 A222,222 0 0 0 470,150 L470,2" />
          <line x1="200" y1="40" x2="300" y2="40" strokeWidth="3" />
        </g>
        <circle cx="250" cy="54" r="8" stroke="#FF9F0A" strokeWidth="2.5" fill="none" />
        {/* shots */}
        {visible.map((s, i) => {
          const color = s.side === "home" ? homeColor : awayColor;
          const cx = px(s.x), cy = py(s.y);
          return s.made ? (
            <circle key={`s${i}-${s.x}-${s.y}`} data-testid={`nba-shot-${i}`} cx={cx} cy={cy} r="5.5" fill={color} fillOpacity="0.9" stroke="#fff" strokeWidth="0.6" />
          ) : (
            <g key={`s${i}-${s.x}-${s.y}`} data-testid={`nba-shot-${i}`} stroke={color} strokeWidth="1.6" opacity="0.65">
              <line x1={cx - 4} y1={cy - 4} x2={cx + 4} y2={cy + 4} />
              <line x1={cx - 4} y1={cy + 4} x2={cx + 4} y2={cy - 4} />
            </g>
          );
        })}
      </svg>
      <p className="text-[10px] text-white/35 mt-2 text-center uppercase tracking-wider">● Made · ✕ Missed — {visible.length} located shots</p>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} /> {label}
    </span>
  );
}

// ---- Win probability timeline ----
function WinProbChart({ wp, away, home, awayColor, homeColor }) {
  const W = 500, H = 170;
  if (!wp?.length) return <div className="text-white/40 text-sm py-10 text-center">No win-probability data.</div>;
  const pts = wp.map((p, i) => {
    const x = (i / (wp.length - 1)) * W;
    const y = (1 - p / 100) * H; // 100% home at top
    return [x, y];
  });
  const line = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `0,${H} ${line} ${W},${H}`;
  const current = wp[wp.length - 1];
  return (
    <div data-testid="nba-winprob-chart">
      <div className="flex items-center justify-between mb-2 text-[11px] font-bold uppercase tracking-wider">
        <span style={{ color: homeColor }}>{home?.abbr} {current}%</span>
        <span className="text-white/35">Win Probability</span>
        <span style={{ color: awayColor }}>{away?.abbr} {100 - current}%</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full rounded-lg" style={{ background: "#0c1620" }}>
        <defs>
          <linearGradient id="wpgrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={homeColor} stopOpacity="0.45" />
            <stop offset="100%" stopColor={homeColor} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <line x1="0" y1={H / 2} x2={W} y2={H / 2} stroke="#3a4d63" strokeWidth="1" strokeDasharray="4 4" />
        <polygon points={area} fill="url(#wpgrad)" />
        <polyline points={line} fill="none" stroke={homeColor} strokeWidth="2.5" strokeLinejoin="round" />
      </svg>
      <p className="text-[10px] text-white/35 mt-2 text-center uppercase tracking-wider">Tip-off → now · line shows {home?.abbr} win %</p>
    </div>
  );
}

// ---- Recent scoring plays ----
function PlaysList({ plays, away, home }) {
  if (!plays?.length) return <div className="text-white/40 text-sm py-10 text-center">No plays yet.</div>;
  return (
    <div className="space-y-1.5" data-testid="nba-plays">
      {plays.map((p, i) => (
        <div key={`${p.period}-${p.clock}-${p.text?.slice(0, 16)}-${i}`} className="flex items-center gap-3 px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.06]">
          <span className="font-num text-xs text-white/50 w-16 shrink-0">{away?.abbr} {p.away}-{p.home} {home?.abbr}</span>
          <span className="text-sm text-white/85 flex-1">{p.text}</span>
          {p.value > 0 && <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-[#32D74B]/15 text-[#32D74B]">+{p.value}</span>}
          <span className="text-[10px] text-white/35 w-10 text-right shrink-0">{p.clock}</span>
        </div>
      ))}
    </div>
  );
}
